/**
 * Telegram Mini App 전용 리포트 화면
 *
 * ExportReport 와 같은 데이터를 쓰지만 표현층이 다르다.
 * 웹은 데스크톱 폭을 전제로 카드·사이드바·푸터를 그리는데, 채팅 위에 뜨는 시트에서는
 * 그게 전부 잡음이다. 여기서는 본문만 남기고 텔레그램 테마를 따른다.
 *
 * 렌더러(ReportRenderer)는 그대로 재사용한다 — 표현층만 갈아끼우는 것이지
 * 리포트 파싱을 다시 만드는 것이 아니다.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, ExternalLink } from 'lucide-react';
import { ReportRenderer } from '../components/reports';
import { convertLatexToUnicode } from '../components/reports/latexUtils';
import { useTelegram, useTelegramTheme, haptic } from '../hooks/useTelegram';
import { FeedbackSurvey } from '../components/telegram/FeedbackSurvey';
import { EasyReadProvider, easyReadItemActions } from '../components/telegram/EasyRead';
import { ShareReport } from '../components/telegram/ShareReport';

const API_URL = '/api';

/** Easy Read 등 원본이 있는 리포트의 링크를 만든다. */
const originalLink = (content) => {
    if (!content?.source_type) return null;
    const { source_type: type, source_id: id } = content;
    let url = content.original_url;
    if (!url && id) {
        if (type === 'youtube') url = `https://www.youtube.com/watch?v=${id}`;
        else if (type === 'paper') url = `https://arxiv.org/abs/${id}`;
    }
    if (!url) return null;
    const label =
        type === 'youtube' ? '원본 영상' : type === 'article' ? '원본 기사' : '원본 논문';
    return { url, label };
};

export const TelegramReport = () => {
    const { hash } = useParams();
    const { webApp } = useTelegram();
    useTelegramTheme();

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState(new Set());

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                const { data } = await axios.get(`${API_URL}/export/${hash}`);
                setReport(data);
            } catch (err) {
                if (err.response?.status === 404) setError('리포트를 찾을 수 없습니다.');
                else if (err.response?.status === 410)
                    setError(err.response.data?.detail || '만료된 링크입니다.');
                else setError('불러오는 중 오류가 발생했습니다.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [hash]);

    const toggleCategory = (reportId, categoryIdx, event) => {
        event.stopPropagation();
        haptic('light');
        const key = `${reportId}-${categoryIdx}`;
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    if (loading) {
        return (
            <div className="tg-screen tg-center">
                <div className="tg-spinner" />
                <p className="tg-hint">불러오는 중…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="tg-screen tg-center">
                <AlertCircle className="w-10 h-10 mb-3" style={{ color: 'var(--tg-hint)' }} />
                <p style={{ color: 'var(--tg-text)' }}>{error}</p>
                <button className="tg-button mt-4" onClick={() => webApp?.close?.()}>
                    닫기
                </button>
            </div>
        );
    }

    if (!report) return null;

    const content = report.report_content || {};
    const original = originalLink(content);
    const reportType = report.report_type || '';
    const isDerivedReport =
        reportType.startsWith('easy_read') || reportType.startsWith('full_report');
    const dateText = report.report_date
        ? new Date(report.report_date).toLocaleDateString('ko-KR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
          })
        : '';

    return (
        <div className="tg-screen">
            {/* 제목 영역. 텔레그램이 이미 헤더를 그리므로 여기서는 최소한만 둔다. */}
            <div className="tg-titlebar">
                <h1 className="tg-title">{report.display_name ?? report.agent_name}</h1>
                {dateText && <p className="tg-hint tg-date">{dateText}</p>}
            </div>

            {/* 이 화면의 주소가 곧 공유 링크다. 링크를 새로 만들 필요가 없다.
                본문을 끝까지 스크롤해야 나오면 쓰이지 않는다 — 제목 바로 아래에 둔다. */}
            <ShareReport title={content.title || report.display_name || report.agent_name} />

            {content.title && (
                <div className="tg-card">
                    <h2 className="tg-paper-title">{convertLatexToUnicode(content.title)}</h2>
                    <div className="tg-links">
                        {content.arxiv_id && (
                            <a
                                href={`https://arxiv.org/abs/${content.arxiv_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                arXiv: {content.arxiv_id}
                                <ExternalLink className="w-3 h-3" />
                            </a>
                        )}
                        {original && (
                            <a href={original.url} target="_blank" rel="noopener noreferrer">
                                {original.label}
                                <ExternalLink className="w-3 h-3" />
                            </a>
                        )}
                    </div>
                    {report.authors?.length > 0 && (
                        <p className="tg-hint tg-authors">{report.authors.join(', ')}</p>
                    )}
                </div>
            )}

            <div className="tg-body">
                {/* 이미 정리된 글(Easy Read·본문 요약)에는 다시 정리를 요청할 것이 없다 */}
                <EasyReadProvider>
                    <ReportRenderer
                        report={report}
                        reportId={report.id}
                        expandedCategories={expandedCategories}
                        onToggleCategory={toggleCategory}
                        itemActions={isDerivedReport ? undefined : easyReadItemActions}
                    />
                </EasyReadProvider>
            </div>

            {/* Personal News 만 피드백을 받는다 — 조정할 파라미터가 이 에이전트에만 있다 */}
            {report.agent_name === 'Personal News' && (
                <FeedbackSurvey reportId={String(report.id)} />
            )}
        </div>
    );
};

export default TelegramReport;
